import React from 'react';
import { motion } from 'framer-motion';
import { ClipboardList, Palette, Rocket } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
const StepCard = ({
  step,
  index
}: {
  step: any;
  index: number;
}) => {
  const isLeft = index % 2 === 0;
  const {
    Icon
  } = step;
  return <div className="relative grid md:grid-cols-2 gap-8 md:gap-16 items-center" data-id="element-81">
      {/* Spacer for zigzag on desktop */}
      {!isLeft && <div className="hidden md:block" data-id="element-82" />}

      <motion.div className={`relative ${isLeft ? '' : 'md:col-start-2'}`} initial={{
      opacity: 0,
      x: isLeft ? -60 : 60
    }} whileInView={{
      opacity: 1,
      x: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.7,
      ease: 'easeOut'
    }} data-id="element-83">
        <div className="relative bg-gradient-to-br from-[#111] to-[#0A0A0A] border border-white/5 rounded-2xl p-8 md:p-10 overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.4)]" data-id="element-84">
          {/* Big watermark number */}
          <div className="absolute -top-6 -right-2 md:-right-4 font-serif font-bold text-[140px] md:text-[180px] leading-none text-primary/10 select-none pointer-events-none" data-id="element-85">
            {step.number}
          </div>

          {/* Optional badge */}
          {step.badge && <motion.div className="absolute top-6 right-6 bg-primary text-background text-xs font-bold tracking-wider px-3 py-1.5 rounded-full shadow-[0_0_20px_rgba(212,175,55,0.5)] z-10" initial={{
          scale: 0,
          rotate: -15
        }} whileInView={{
          scale: 1,
          rotate: -8
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.8,
          type: 'spring',
          bounce: 0.6
        }} data-id="element-86">
              {step.badge}
            </motion.div>}

          <div className="relative z-10" data-id="element-87">
            {/* Step label */}
            <div className="text-primary text-sm font-bold tracking-[0.3em] mb-4" data-id="element-88">
              PASO {step.number}
            </div>

            {/* Icon with bounce */}
            <motion.div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center text-primary mb-6" initial={{
            scale: 0,
            rotate: -180
          }} whileInView={{
            scale: 1,
            rotate: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3,
            type: 'spring',
            bounce: 0.5,
            duration: 0.6
          }} data-id="element-89">
              <Icon size={32} strokeWidth={1.5} data-id="element-90" />
            </motion.div>

            {/* Title */}
            <h3 className="text-2xl md:text-3xl font-serif font-bold text-white mb-4 leading-tight" data-id="element-91">
              {step.title}
            </h3>

            {/* Description */}
            <p className="text-gray-400 leading-relaxed mb-6" data-id="element-92">
              {step.description}
            </p>

            {/* Bullets */}
            <ul className="space-y-2.5 mb-8" data-id="element-93">
              {step.bullets.map((bullet: string, i: number) => <motion.li key={i} className="flex items-start gap-3 text-gray-300" initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.5 + i * 0.08,
              duration: 0.4
            }} data-id="element-94">
                  <span className="text-primary mt-1" data-id="element-95">✦</span>
                  <span data-id="element-96">{bullet}</span>
                </motion.li>)}
            </ul>

            {/* Tip */}
            <div className="border-l-2 border-primary/60 pl-5 py-2 bg-primary/5 rounded-r-lg" data-id="element-97">
              <p className="text-sm text-gray-300 leading-relaxed" data-id="element-98">
                <span className="text-primary mr-2" data-id="element-99">💡</span>
                {step.tip}
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
};
export const HowItWorks = () => {
  const {
    t
  } = useLanguage();
  const transSteps = t('howItWorks.steps');
  const steps = [{
    number: '01',
    Icon: ClipboardList,
    title: transSteps[0].title,
    description: transSteps[0].description,
    bullets: transSteps[0].bullets,
    tip: transSteps[0].tip
  }, {
    number: '02',
    Icon: Palette,
    title: transSteps[1].title,
    description: transSteps[1].description,
    bullets: transSteps[1].bullets,
    tip: transSteps[1].tip
  }, {
    number: '03',
    Icon: Rocket,
    title: transSteps[2].title,
    description: transSteps[2].description,
    bullets: transSteps[2].bullets,
    tip: transSteps[2].tip,
    badge: transSteps[2].badge
  }];
  return <section className="relative py-32 bg-background overflow-hidden" data-id="element-100">
      {/* Subtle radial glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/[0.04] via-transparent to-transparent pointer-events-none" data-id="element-101" />

      <div className="container mx-auto px-6 relative z-10" data-id="element-102">
        {/* Heading */}
        <div className="text-center mb-20 md:mb-28" data-id="element-103">
          <motion.h2 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-104">
            {t('howItWorks.heading')}
          </motion.h2>
          <motion.p className="text-xl md:text-2xl text-text-secondary font-serif italic" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.5,
          duration: 0.8
        }} data-id="element-105">
            {t('howItWorks.subheading')}
          </motion.p>
        </div>

        {/* Steps Timeline */}
        <div className="relative max-w-6xl mx-auto" data-id="element-106">
          {/* Animated connector line - desktop */}
          <motion.div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary/40 to-transparent -translate-x-1/2 origin-top" initial={{
          scaleY: 0
        }} whileInView={{
          scaleY: 1
        }} viewport={{
          once: true,
          margin: '-200px'
        }} transition={{
          duration: 2,
          ease: 'easeInOut'
        }} data-id="element-107" />

          {/* Mobile connector */}
          <motion.div className="md:hidden absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary/40 to-transparent origin-top" initial={{
          scaleY: 0
        }} whileInView={{
          scaleY: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1.5,
          ease: 'easeInOut'
        }} data-id="element-108" />

          <div className="space-y-16 md:space-y-24" data-id="element-109">
            {steps.map((step, i) => <StepCard key={i} step={step} index={i} data-id="element-110" />)}
          </div>
        </div>

        {/* Mini CTA */}
        <motion.div className="text-center mt-24 md:mt-32" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} data-id="element-111">
          <h3 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-112">
            {t('howItWorks.miniCtaHeading')}
          </h3>
          <p className="text-lg text-text-secondary mb-10 max-w-xl mx-auto" data-id="element-113">
            {t('howItWorks.miniCtaSub')}
          </p>
          <motion.button className="relative group overflow-hidden bg-primary text-background font-bold text-base tracking-widest py-4 px-10 rounded-full shadow-[0_0_25px_rgba(212,175,55,0.5)] hover:shadow-[0_0_45px_rgba(212,175,55,0.8)] transition-all duration-300 shimmer animate-pulse-slow" whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={() => window.open('https://wa.me/5492236514357?utm_source=chatgpt.com', '_blank', 'noopener,noreferrer')} data-id="element-114">
            {t('howItWorks.miniCtaBtn')}
          </motion.button>
        </motion.div>
      </div>
    </section>;
};